import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Activity, DailyChallenge, Opponent, PlayerStats } from '../types/activity';
import { getDaysFromCycleStart, isToday } from '../utils/dateUtils';
import { sumPoints } from '../utils/scoring';
import CalendarDay from './CalendarDay';

const PEEK = 28;
const GAP  = 12;
const MOBILE_BP = 700;

interface Props {
  cycleStart: string;
  workoutDeadline?: string;
  myActivities: Activity[];
  opponentActivities: Activity[];
  weekChallenges?: Record<string, DailyChallenge>;
  opponent?: Opponent | null;
  myName?: string;
  myAvatar?: string | null;
  myIsBot?: boolean;
  myStats?: PlayerStats | null | false;
  opponentStats?: PlayerStats | null | false;
  myConsolationPoints?: number;
  opponentConsolationPoints?: number;
  hidePoints?: boolean;
  hideOpponentPoints?: boolean;
  hideSpin?: boolean;
  disableChipTooltips?: boolean;
  hideOpponentTooltips?: boolean;
  hideChallengeEarned?: boolean;
  onDayChange?: (dayIndex: number) => void;
  bottomSlot?: React.ReactNode;
  maxSpinDays?: number;
  spinStartDate?: string | null;
  spinItemId?: number | null;
  myId?: number;
  onNavigateToPlayerProfile?: (playerId: number) => void;
}

export default function WeeklyCalendar({ cycleStart, workoutDeadline, myActivities, opponentActivities, weekChallenges = {}, opponent, myName = 'You', myAvatar, myIsBot = false, myStats, opponentStats, myConsolationPoints = 0, opponentConsolationPoints = 0, hidePoints = false, hideOpponentPoints = false, hideSpin = false, disableChipTooltips = false, hideOpponentTooltips = false, hideChallengeEarned = false, onDayChange, bottomSlot, maxSpinDays = 1, spinStartDate = null, spinItemId = null, myId, onNavigateToPlayerProfile }: Props) {
  const days        = getDaysFromCycleStart(cycleStart);

  // Punkte während laufendem Duell verstecken (Consolation-Punkte bleiben sichtbar = kein Spoiler)
  const maskPoints = (acts: Activity[]): Activity[] =>
    hidePoints ? acts.map(a => (a.sport_type === 'Consolation' || a.sport_type === 'BadgeReward') ? a : { ...a, total_points: null as any, base_points: null as any, heart_rate_bonus_points: 0 }) : acts;
  const visibleMyActs  = maskPoints(myActivities);
  const visibleOppActs = maskPoints(opponentActivities);

  const totalPoints = sumPoints(myActivities);

  const todayIdx = days.findIndex(d => isToday(d));
  const [centerIndex, setCenterIndex]   = useState(todayIdx >= 0 ? todayIdx : 0);
  const [isMobile, setIsMobile]         = useState(false);
  const [cardWidth, setCardWidth]       = useState(300);
  const outerRef    = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const animated    = useRef(false);

  const updateDimensions = useCallback(() => {
    const mobile = window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches;
    setIsMobile(mobile);
    if (outerRef.current) {
      setCardWidth(outerRef.current.offsetWidth - 2 * PEEK);
    }
  }, []);

  useEffect(() => {
    updateDimensions();
    animated.current = true;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
    mq.addEventListener('change', updateDimensions);
    window.addEventListener('resize', updateDimensions);
    const ro = new ResizeObserver(updateDimensions);
    if (outerRef.current) ro.observe(outerRef.current);
    return () => {
      mq.removeEventListener('change', updateDimensions);
      window.removeEventListener('resize', updateDimensions);
      ro.disconnect();
    };
  }, [updateDimensions]);

  const setDay = useCallback((idx: number) => { setCenterIndex(idx); onDayChange?.(idx); }, [onDayChange]);
  const prev = () => setCenterIndex(i => { const n = Math.max(0, i - 1); onDayChange?.(n); return n; });
  const next = () => setCenterIndex(i => { const n = Math.min(days.length - 1, i + 1); onDayChange?.(n); return n; });

  const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    if (dx > 40) next();
    else if (dx < -40) prev();
    touchStartX.current = null;
  };

  const trackOffset = PEEK - centerIndex * (cardWidth + GAP);
  const trackStyle: React.CSSProperties = isMobile
    ? {
        transform:  `translateX(${trackOffset}px)`,
        transition: animated.current ? 'transform 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
        willChange: 'transform',
      }
    : {};

  const outerStyle: React.CSSProperties = isMobile
    ? { '--mobile-card-w': `${cardWidth}px` } as React.CSSProperties
    : {};

  return (
    <div className="calendar">
      <div
        ref={outerRef}
        className="calendar-outer"
        style={outerStyle}
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
      >
        <div className="calendar-track" style={trackStyle}>
          {days.map((day, i) => {
            const dateKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
            return (
              <CalendarDay
                key={i}
                day={day}
                dayIndex={i}
                myActivities={visibleMyActs}
                opponentActivities={visibleOppActs}
                today={isToday(day)}
                isCenter={i === centerIndex}
                challenge={(function() {
                  const d = new Date(day); d.setHours(0,0,0,0);
                  const t = new Date(); t.setHours(0,0,0,0);
                  const diff = Math.round((d.getTime() - t.getTime()) / 86400000);
                  return diff <= 0 ? (weekChallenges[dateKey] ?? null) : null;
                })()}
                workoutDeadline={workoutDeadline}
                opponentAvatar={opponent?.avatar_url ?? null}
                opponentIsBot={!!opponent?.is_bot}
                opponentName={opponent?.display_name ?? 'Opponent'}
                opponentId={opponent?.id}
                myName={myName}
                myAvatar={myAvatar ?? null}
                myIsBot={myIsBot}
                myStats={myStats}
                opponentStats={opponentStats}
                hidePoints={hidePoints}
                hideOpponentPoints={hideOpponentPoints}
                hideSpin={hideSpin}
                disableChipTooltips={disableChipTooltips}
                hideOpponentTooltips={hideOpponentTooltips}
                hideChallengeEarned={hideChallengeEarned}
                maxSpinDays={maxSpinDays}
                spinStartDate={spinStartDate}
                spinItemId={spinItemId}
                myId={myId}
                onNavigateToPlayerProfile={onNavigateToPlayerProfile}
              />
            );
          })}
        </div>
      </div>

      {bottomSlot}

      {isMobile && (
        <div className="carousel-dots">
          {days.map((d, i) => (
            <button
              key={i}
              className={`carousel-dot${i === centerIndex ? ' carousel-dot--active' : ''}${isToday(d) ? ' carousel-dot--today' : ''}`}
              onClick={() => setDay(i)}
              aria-label={`Tag ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
